import React from "react";
import { Copy, Check, ImageOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { useLocalCopyToClipboard } from "./useLocalCopyToClipboard";

// EMAILS-IMPL-020: dedicated GHL Handoff view -- renders ONLY the
// persisted, immutable Rendered Handoff Snapshot (BRD-SPEC-001 §13.1)
// returned by the backend. This component never re-fetches current
// Creative/Asset content, never reconstructs `tracking_url`, and never
// normalizes the recipient State merge token -- see this file's own
// per-field comments below for exactly where that governed boundary is
// enforced structurally, not just by convention.

// EMAILS-IMPL-032 (EMAILS-SPEC-017/017B): CSM/RIN added -- their uppercased
// labels ("CTA SUPPORT MICROCOPY:"/"REPLY INSTRUCTIONS:") appear in the
// Audit Transcript exactly like every other family's own label already
// does (buildBodyText below); the Composed Body transform
// (buildComposedBodyText) never prepends any family label, for any
// family, unchanged.
const FAMILY_LABELS = {
  HOK: "Hook", EML: "Email Body", OFF: "Offer", CTA: "CTA", PS: "P.S.", IMG: "Image",
  CSM: "CTA Support Microcopy", RIN: "Reply Instructions",
};

function familyLabel(block) {
  const base = FAMILY_LABELS[block.family] || block.family;
  return block.role === "echo" ? `${base} (echo)` : base;
}

// Deterministic, readable plain text built ONLY from the persisted
// Snapshot's own `body` array, in its own persisted order -- never
// recalculated from a live Creative. Image blocks carry no `content`
// (see RenderedHandoffBodyBlock's own contract) and are naturally
// excluded here.
//
// This is the AUDIT TRANSCRIPT representation -- family-labeled, for
// governance/proof purposes. Its output is deliberately left byte-for-byte
// unchanged by EMAILS-IMPL-031 (see `buildComposedBodyText` below for the
// separate, frictionless Day-1 transfer representation).
function buildBodyText(snapshot) {
  return (snapshot.body || [])
    .filter((block) => block.content)
    .map((block) => `${familyLabel(block).toUpperCase()}:\n${block.content}`)
    .join("\n\n");
}

// EMAILS-IMPL-031: the COMPOSED BODY representation -- what an operator
// actually transfers into GHL Email Builder. A pure transform over the
// SAME already-persisted `snapshot.body` array `buildBodyText` reads --
// no network request, no live Creative/Asset resolution, no regeneration.
// Unlike the audit transcript, this never prepends a family label and
// never drops an Image block: a block with no `content` (IMG only, per
// RenderedHandoffBodyBlock's own contract) gets a deterministic manual-
// recreation marker built ONLY from that block's own frozen `asset_id`/
// `version` -- never a fabricated alt text, URL, or styling.
function buildComposedBodyText(snapshot) {
  return (snapshot.body || [])
    .map((block) =>
      block.content ? block.content : `[IMAGE — manual recreation required: ${block.asset_id} v${block.version}]`
    )
    .join("\n\n");
}

// EMAILS-IMPL-033: the EMAIL FOOTER section of the GHL Transfer Package --
// frozen footer/company content only, in the exact conceptual order
// specified by the checkpoint. Optional fields (legal entity, terms of
// use, compliance instructions) are omitted entirely, never rendered as
// an empty line, when the frozen Snapshot value is absent.
function buildFooterSectionText(snapshot) {
  const lines = [`BRAND NAME\n${snapshot.brand_name}`];
  if (snapshot.legal_entity_name) lines.push(`LEGAL ENTITY\n${snapshot.legal_entity_name}`);
  lines.push(`COPYRIGHT\n${snapshot.copyright_text}`);
  lines.push(`MAILING ADDRESS\n${snapshot.mailing_address}`);
  lines.push(`PRIVACY POLICY URL\n${snapshot.privacy_policy_url}`);
  if (snapshot.terms_of_use_url) lines.push(`TERMS OF USE URL\n${snapshot.terms_of_use_url}`);
  if (snapshot.compliance_instructional_text) lines.push(`COMPLIANCE INSTRUCTIONS\n${snapshot.compliance_instructional_text}`);
  return lines.join("\n\n");
}

// EMAILS-IMPL-031: the GHL Transfer Package -- a deterministic plain-text
// operator package (never persisted governed content itself) combining the
// Composed Body with every other field an operator needs to manually
// recreate this email in GHL. Section order is fixed; `tracking_url` is
// copied verbatim (the literal {{contact.id}}/{{contact.state}} merge
// tokens are never escaped, re-encoded, or otherwise altered), matching
// this file's existing tracking-URL discipline exactly.
//
// EMAILS-IMPL-033: extends this SAME package with an EMAIL FOOTER section
// plus an EXECUTION PLATFORM REQUIREMENT reminder -- but ONLY when the
// Snapshot actually carries footer data (`brand_name` present). A
// historical Snapshot generated before this checkpoint existed (e.g.
// BRC0009) carries no footer fields at all, so its own package output
// remains byte-for-byte unchanged, per the checkpoint's explicit backward-
// compatibility requirement. The reminder text never names a specific
// unsubscribe/preference URL, GHL merge syntax, or SendGrid token --
// MissionAssist does not own or generate that functional mechanism.
function buildGhlTransferPackage(snapshot) {
  const sections = [
    `FROM NAME\n${snapshot.from_name}`,
    `FROM ADDRESS\n${snapshot.from_address}`,
    `REPLY-TO\n${snapshot.reply_to}`,
    `SUBJECT\n${snapshot.subject}`,
    `PREVIEW TEXT\n${snapshot.preview_text}`,
    `COMPOSED BODY\n${buildComposedBodyText(snapshot)}`,
    `CTA TEXT\n${snapshot.cta_text}`,
    `TRACKING URL\n${snapshot.tracking_url}`,
  ];
  if (snapshot.brand_name) {
    sections.push(`EMAIL FOOTER\n${buildFooterSectionText(snapshot)}`);
    sections.push(
      "EXECUTION PLATFORM REQUIREMENT\n" +
        "This footer content is frozen, visible company/compliance copy only. Do not fabricate an unsubscribe or " +
        "preference-management link, GHL merge syntax, or a SendGrid token — the execution platform (GHL/SendGrid) " +
        "supplies the functional unsubscribe/preference mechanism at send time."
    );
  }
  return sections.join("\n\n");
}

function buildCopyAllText(snapshot) {
  return [
    `From Name:\n${snapshot.from_name}`,
    `From Address:\n${snapshot.from_address}`,
    `Reply-To:\n${snapshot.reply_to}`,
    `Subject:\n${snapshot.subject}`,
    `Preview:\n${snapshot.preview_text}`,
    `Body:\n${buildBodyText(snapshot)}`,
    `CTA:\n${snapshot.cta_text}`,
    `URL:\n${snapshot.tracking_url}`,
  ].join("\n\n");
}

function CopyableField({ label, value, testId, multiline, copyLabel = "Copy", copiedLabel = "Copied", buttonClassName }) {
  const { copied, copy } = useLocalCopyToClipboard();
  return (
    <div className="space-y-1.5 rounded-md border border-white/10 bg-white/5 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={buttonClassName || "h-7 gap-1.5 border-white/10 px-2 text-xs text-slate-200"}
          onClick={() => copy(value)}
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? copiedLabel : copyLabel}
        </Button>
      </div>
      {/* Plain text node -- React inserts `value` as-is, with no HTML-entity
          transform or URL encoding, so a literal `{{contact.id}}`/
          `{{contact.state}}` merge token renders and reads exactly as
          persisted. */}
      <div
        className={`text-sm text-slate-100 ${multiline ? "whitespace-pre-wrap" : "break-all font-mono"}`}
        data-testid={`${testId}-value`}
      >
        {value}
      </div>
    </div>
  );
}

function ImageBlockCard({ block }) {
  const img = block.image;
  if (!img) return null;
  return (
    <div className="space-y-1 rounded-md border border-amber-400/20 bg-amber-500/5 p-3" data-testid={`handoff-image-${block.block_id}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-amber-300">
        <ImageOff className="h-3.5 w-3.5" /> Image — manual recreation required
      </div>
      <p className="text-xs text-amber-200/80">
        No public image URL is available for Day-1 handoff. Manually re-add this image inside GHL using the metadata below.
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300 sm:grid-cols-3">
        <Field label="Image ID" value={`${img.image_id} v${img.version}`} mono />
        <Field label="Alt text" value={img.alt_text || "—"} />
        <Field label="Content type" value={img.content_type} />
        <Field
          label="Dimensions"
          value={img.intrinsic_width && img.intrinsic_height ? `${img.intrinsic_width}×${img.intrinsic_height}px` : "—"}
        />
        <Field label="Placement" value={img.placement || "—"} />
        <Field label="Presentation width" value={img.presentation_width ? `${img.presentation_width}px` : "—"} />
        <Field label="Alignment" value={img.alignment || "—"} />
      </dl>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</dd>
    </div>
  );
}

export default function BroadcastHandoffView({ open, onOpenChange, broadcast, snapshot }) {
  const { copied: copiedAll, copy: copyAll } = useLocalCopyToClipboard();

  if (!snapshot) return null;

  const bodyText = buildBodyText(snapshot);
  const composedBodyText = buildComposedBodyText(snapshot);
  const nonImageBlocks = (snapshot.body || []).filter((b) => b.family !== "IMG");
  const imageBlocks = (snapshot.body || []).filter((b) => b.family === "IMG");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-slate-950 text-slate-100 sm:max-w-2xl" data-testid="broadcast-handoff-view">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="text-slate-100">GHL Handoff</DialogTitle>
            {broadcast?.environment ? (
              <Badge
                variant="outline"
                className={
                  broadcast.environment === "Production"
                    ? "border-rose-400/40 bg-rose-500/15 text-rose-200"
                    : "border-sky-400/40 bg-sky-500/15 text-sky-200"
                }
                data-testid="broadcast-handoff-environment"
              >
                {broadcast.environment}
              </Badge>
            ) : null}
          </div>
          <DialogDescription className="text-slate-400">
            Copy the governed content and tracking URL below to recreate this email manually inside GHL's own Email Builder. This
            is the immutable, historical handoff record for {snapshot.broadcast_id} — it is never regenerated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Provenance -- reference metadata, kept out of the primary copy workflow. */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-white/10 bg-white/5 p-3 text-xs sm:grid-cols-3" data-testid="handoff-provenance">
            <Field label="Broadcast ID" value={snapshot.broadcast_id} mono />
            <Field label="Creative ID" value={snapshot.creative_id} mono />
            <Field label="Creative Version" value={snapshot.creative_version} mono />
            <Field label="Generated" value={snapshot.generated_at ? new Date(snapshot.generated_at).toLocaleString() : "—"} />
            <Field label="Landing Page" value={`${snapshot.landing_page_asset_id} v${snapshot.landing_page_asset_version}`} mono />
          </dl>

          {/* Sender identity -- EMAILS-IMPL-021: copied verbatim from the
              persisted Snapshot's own frozen `from_name`/`from_address`/
              `reply_to` fields only. This component never re-fetches the
              source Sender Configuration, so a later Retire/edit of that
              configuration (edits are only possible pre-Approval, but
              Retire is always possible) can never change what renders
              here. */}
          <CopyableField label="From Name" value={snapshot.from_name} testId="handoff-from-name" />
          <CopyableField label="From Address" value={snapshot.from_address} testId="handoff-from-address" />
          <CopyableField label="Reply-To" value={snapshot.reply_to} testId="handoff-reply-to" />

          <CopyableField label="Subject" value={snapshot.subject} testId="handoff-subject" />
          <CopyableField label="Preview Text" value={snapshot.preview_text} testId="handoff-preview" />

          {/* EMAILS-IMPL-031: COMPOSED BODY -- the primary Day-1 transfer
              representation. A pure transform of the SAME persisted
              `snapshot.body` array the Audit Transcript below reads --
              content only, in persisted order, no family labels, no
              silently-dropped Image blocks (see buildComposedBodyText).
              This is plain text only -- never rendered HTML, and never
              implying styling fidelity GHL Email Builder will preserve. */}
          <div className="space-y-1.5 rounded-md border border-violet-400/30 bg-violet-500/5 p-3" data-testid="handoff-composed-body-section">
            <div className="text-xs font-medium uppercase tracking-wide text-violet-300">Composed Body</div>
            <p className="text-[11px] text-slate-400">
              Clean, unlabeled body copy in governed order — this is what to paste into GHL Email Builder. Plain text only, no
              styling.
            </p>
            <CopyableField
              label="Composed Body"
              value={composedBodyText}
              testId="handoff-composed-body"
              multiline
              copyLabel="Copy Composed Body"
              copiedLabel="Composed Body Copied"
              buttonClassName="h-7 gap-1.5 border-violet-400/30 bg-violet-500/10 px-2 text-xs text-violet-100 hover:bg-violet-500/20"
            />
          </div>

          <CopyableField
            label="Audit Transcript"
            value={bodyText}
            testId="handoff-body"
            multiline
            copyLabel="Copy Audit Transcript"
            copiedLabel="Audit Transcript Copied"
          />

          {/* Visual body-block breakdown, in persisted Canvas/fallback
              order -- reference only; "Copy Audit Transcript" above already
              copies the deterministic full text. */}
          {nonImageBlocks.length > 0 && (
            <div className="space-y-1.5" data-testid="handoff-body-blocks">
              {nonImageBlocks.map((block) => (
                <div key={block.block_id} className="rounded border border-white/5 bg-white/[0.02] p-2 text-xs text-slate-400">
                  <span className="font-medium text-slate-300">{familyLabel(block)}</span>
                  <span className="ml-2 font-mono text-slate-500">
                    {block.asset_id} v{block.version}
                  </span>
                </div>
              ))}
            </div>
          )}

          {imageBlocks.map((block) => (
            <ImageBlockCard key={block.block_id} block={block} />
          ))}

          <CopyableField label="CTA Text" value={snapshot.cta_text} testId="handoff-cta" />

          {/* Frozen base LPG destination -- reference only, distinct from
              the final tracking URL below (the actual value to place in
              GHL's CTA link). */}
          <div className="space-y-1 rounded-md border border-white/10 bg-white/5 p-3 text-xs" data-testid="handoff-landing-page-destination">
            <span className="uppercase tracking-wide text-slate-500">Landing Page destination (reference)</span>
            <div className="break-all font-mono text-slate-300">{snapshot.landing_page_destination_url}</div>
            <p className="text-[11px] text-slate-500">
              Reference only — do not use this for the GHL CTA link. Use the governed <strong>Tracking URL</strong> below.
            </p>
          </div>

          {/* Tracking URL — the ABSOLUTE RULE (checkpoint §21/§22): this is
              `snapshot.tracking_url`, rendered and copied verbatim.
              Nothing in this component parses, reconstructs, appends to,
              or normalizes this string; the literal {{contact.id}}/
              {{contact.state}} GHL merge tokens are never escaped, never
              URL-encoded, and never resolved here. */}
          <div className="space-y-1.5 rounded-md border border-violet-400/30 bg-violet-500/10 p-3" data-testid="handoff-tracking-url">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-violet-300">Tracking URL</span>
              <Button
                type="button"
                size="sm"
                className="h-7 gap-1.5 bg-violet-600 px-2 text-xs text-white hover:bg-violet-500"
                onClick={() => copyAll(snapshot.tracking_url)}
                data-testid="handoff-tracking-url-copy"
              >
                {copiedAll ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copiedAll ? "Copied" : "Copy URL"}
              </Button>
            </div>
            <div className="break-all font-mono text-sm text-slate-100" data-testid="handoff-tracking-url-value">
              {snapshot.tracking_url}
            </div>
          </div>

          {/* EMAILS-IMPL-033: EMAIL FOOTER / COMPANY INFORMATION -- frozen
              footer/company content only, deliberately kept separate from
              the Composed Body/Audit Transcript above (Creative content).
              Rendered ONLY when this Snapshot actually carries footer
              data (`brand_name` present) -- a historical Snapshot
              generated before this checkpoint existed (e.g. BRC0009)
              renders exactly as it did before, with no fabricated footer
              content. Optional fields are each conditionally rendered
              only when the frozen value is present. */}
          {snapshot.brand_name && (
            <div className="space-y-1.5 rounded-md border border-white/10 bg-white/5 p-3" data-testid="handoff-footer-section">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Email Footer / Company Information</div>
              <p className="text-[11px] text-slate-500">
                Frozen footer/company content only — MissionAssist does not generate the functional unsubscribe or
                preference-management link; GHL/SendGrid supplies that at send time.
              </p>
              <CopyableField label="Brand Name" value={snapshot.brand_name} testId="handoff-footer-brand-name" />
              {snapshot.legal_entity_name && (
                <CopyableField label="Legal Entity" value={snapshot.legal_entity_name} testId="handoff-footer-legal-entity" />
              )}
              <CopyableField label="Copyright" value={snapshot.copyright_text} testId="handoff-footer-copyright" />
              <CopyableField label="Mailing Address" value={snapshot.mailing_address} testId="handoff-footer-mailing-address" multiline />
              <CopyableField label="Privacy Policy URL" value={snapshot.privacy_policy_url} testId="handoff-footer-privacy-policy-url" />
              {snapshot.terms_of_use_url && (
                <CopyableField label="Terms of Use URL" value={snapshot.terms_of_use_url} testId="handoff-footer-terms-of-use-url" />
              )}
              {snapshot.compliance_instructional_text && (
                <CopyableField
                  label="Compliance Instructional Text"
                  value={snapshot.compliance_instructional_text}
                  testId="handoff-footer-compliance-instructional-text"
                  multiline
                />
              )}
            </div>
          )}

          <CopyGhlTransferPackageButton snapshot={snapshot} />
          <CopyAllButton snapshot={snapshot} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// EMAILS-IMPL-031: the primary Day-1 transfer package -- deterministic
// plain text built from `buildGhlTransferPackage` (Composed Body, never
// the Audit Transcript). Distinct from, and does not replace, "Copy All
// (plain text)" below, which retains its existing audit-transcript-based
// behavior unchanged for backward compatibility.
function CopyGhlTransferPackageButton({ snapshot }) {
  const { copied, copy } = useLocalCopyToClipboard();
  return (
    <Button
      type="button"
      className="w-full gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
      onClick={() => copy(buildGhlTransferPackage(snapshot))}
      data-testid="handoff-copy-ghl-package"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? "Copied GHL transfer package" : "Copy GHL Transfer Package"}
    </Button>
  );
}

function CopyAllButton({ snapshot }) {
  const { copied, copy } = useLocalCopyToClipboard();
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full gap-1.5 border-white/10 text-slate-200"
      onClick={() => copy(buildCopyAllText(snapshot))}
      data-testid="handoff-copy-all"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      {copied ? "Copied all fields" : "Copy All (plain text)"}
    </Button>
  );
}
